import { KYCVerificationIcon } from 'core/components/Icons'
import { Badge, BadgeTone } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import React from 'react'
import { KycListItem } from '../../domain/entities/Kyc'

interface KycQueueProps {
  records: KycListItem[]
  loading: boolean
  onReview: (record: KycListItem) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
}

type KycStatus = 'verified' | 'rejected' | 'pending'

const statusTones: Record<KycStatus, BadgeTone> = {
  verified: 'success',
  rejected: 'danger',
  pending: 'warning',
}

const statusLabels: Record<KycStatus, string> = {
  verified: 'Verified',
  rejected: 'Rejected',
  pending: 'Pending Review',
}

const getStatus = (row: KycListItem): KycStatus => {
  if (row.verified) return 'verified'
  if (row.rejected) return 'rejected'
  return 'pending'
}

export const KycQueue: React.FC<KycQueueProps> = ({
  records,
  loading,
  onReview,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const columns: Column<KycListItem>[] = [
    {
      key: 'id',
      label: 'KYC ID',
      className: 'font-semibold text-text-primary',
    },
    {
      key: 'businessName',
      label: 'Business Profile',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-text-primary">{row.businessName}</span>
          <span className="text-xs text-text-secondary">{row.proprietorName}</span>
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Contact Info',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-text-primary">{row.phone}</span>
          <span className="text-xs text-text-secondary">{row.email || '—'}</span>
        </div>
      ),
    },
    {
      key: 'gstNumber',
      label: 'GST Number',
      render: (row) => (
        <span className="font-mono text-text-secondary text-sm">{row.gstinLast4 ? `•••• ${row.gstinLast4}` : '—'}</span>
      ),
    },
    {
      key: 'consentGivenAt',
      label: 'Consent',
      render: (row) => (
        <Badge tone={row.consentGivenAt ? 'success' : 'neutral'} variant="outline" className="capitalize">
          {row.consentGivenAt ? 'Accepted' : 'Pending'}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'KYC Status',
      render: (row) => {
        const status = getStatus(row)
        return (
          <Badge tone={statusTones[status]} variant="subtle">
            {statusLabels[status]}
          </Badge>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => onReview(row)}
            className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            Review Details
          </button>
        </div>
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      data={records}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage="No KYC records pending in this queue."
      emptyIcon={<KYCVerificationIcon size={20} className="opacity-60" />}
    />
  )
}
